import { sanitizeSvg, svgDataUrl } from '../../supabase/functions/_shared/svg-sanitize';

/**
 * Rasterise an SVG to a PNG in the browser (canvas), for a logo that works
 * everywhere (emails, social previews). The SVG is sanitised again first and
 * loaded through an <img> from a data: URL, so nothing in it can run.
 * The PNG is drawn at `scale`× the SVG's own size, on a transparent background.
 */
export async function rasteriseSvgToPng(svg: string, scale = 2, maxSide = 1600): Promise<Blob> {
  const clean = sanitizeSvg(svg);
  if (!clean.ok) throw new Error('That logo couldn’t be prepared.');

  const ratio = clean.width / clean.height;
  let width = Math.round(clean.width * scale);
  let height = Math.round(clean.height * scale);
  if (Math.max(width, height) > maxSide) {
    if (ratio >= 1) {
      width = maxSide;
      height = Math.round(maxSide / ratio);
    } else {
      height = maxSide;
      width = Math.round(maxSide * ratio);
    }
  }

  const image = new Image();
  image.decoding = 'async';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('That logo couldn’t be drawn.'));
    image.src = svgDataUrl(clean.svg);
  });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Your browser couldn’t draw the logo.');
  ctx.drawImage(image, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Your browser couldn’t save the logo.'))), 'image/png');
  });
}
